import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import markUrl from "~/assets/mark-gold-128.png";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { authClient } from "~/lib/auth-client";
import { useT } from "~/lib/i18n";
import type { MessageKey } from "@erp/i18n";

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: Login,
});

function safeRedirect(target: string | undefined) {
  // Only same-app paths: never bounce to an external URL taken from the query string.
  return target && target.startsWith("/") && !target.startsWith("//") ? target : "/";
}

function errorKey(status: number | undefined): MessageKey {
  if (status === 429) return "errors.rateLimited";
  if (status === 401 || status === 400 || status === 403) return "errors.codes.auth.invalid_credentials";
  return status ? "errors.generic" : "errors.network";
}

function Login() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();
  const [step, setStep] = useState<"credentials" | "totp">("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<MessageKey | null>(null);
  const [pending, setPending] = useState(false);

  const finish = async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    await navigate({ href: safeRedirect(redirect), replace: true });
  };

  const submitCredentials = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    const { data, error: failure } = await authClient.signIn.email({ email, password });
    setPending(false);
    if (failure) return setError(errorKey(failure.status));
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) return setStep("totp");
    await finish();
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    const { error: failure } = await authClient.twoFactor.verifyTotp({ code, trustDevice: true });
    setPending(false);
    if (failure) return setError(failure.status === 429 ? "errors.rateLimited" : "errors.codes.auth.two_factor_invalid");
    await finish();
  };

  return (
    <div className="grid min-h-dvh md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <div className="relative flex items-center gap-3 overflow-hidden bg-ink px-6 pt-[calc(env(safe-area-inset-top)+2rem)] pb-8 text-white md:flex-col md:items-start md:justify-between md:p-12">
        <div className="flex items-center gap-3">
          <img src={markUrl} alt="" className="size-10" />
          <span className="font-display text-xl font-medium">{t("app.shortName")}</span>
        </div>
        <img
          src={markUrl}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-24 -bottom-20 w-[520px] opacity-[0.07] max-md:hidden"
        />
        <p className="max-w-xs font-display text-[28px] leading-tight font-normal text-[#dfe6ee] max-md:hidden">
          {t("app.tagline")}
        </p>
      </div>

      <div className="flex items-start justify-center px-6 py-10 md:items-center md:py-12">
        {step === "credentials" ? (
          <form onSubmit={submitCredentials} className="flex w-full max-w-sm flex-col gap-5" noValidate>
            <div>
              <h1 className="text-2xl font-medium">{t("auth.signInTitle")}</h1>
              <p className="mt-1 text-fg-2">{t("auth.signInSubtitle")}</p>
            </div>
            <Field label={t("auth.email")}>
              <Input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </Field>
            <Field label={t("auth.password")}>
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? (
              <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
                {t(error)}
              </p>
            ) : null}
            <Button type="submit" variant="primary" size="lg" loading={pending} disabled={!email || !password}>
              {pending ? t("auth.signingIn") : t("auth.signIn")}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="flex w-full max-w-sm flex-col gap-5" noValidate>
            <div>
              <h1 className="text-2xl font-medium">{t("auth.twoFactorTitle")}</h1>
              <p className="mt-1 text-fg-2">{t("auth.twoFactorSubtitle")}</p>
            </div>
            <Field label={t("auth.twoFactorCode")}>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-lg tracking-[0.4em] tabular"
                autoFocus
              />
            </Field>
            {error ? (
              <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
                {t(error)}
              </p>
            ) : null}
            <Button type="submit" variant="primary" size="lg" loading={pending} disabled={code.length !== 6}>
              {t("auth.verify")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
